import AgendaEmailAddress from "@/domain/entities/agenda/AgendaEmailAddress";
import AgendaItem, {
    AgendaItemImportance,
    AgendaItemSensitivity,
    AgendaItemShowAs,
    AgendaItemType,
} from "@/domain/entities/agenda/AgendaItem";
import AgendaItemAttendee from "@/domain/entities/agenda/AgendaItemAttendee";
import AgendaItemOrganizer from "@/domain/entities/agenda/AgendaItemOrganizer";
import CreateAgendaItemData from "@/domain/entities/agenda/CreateAgendaItemData";
import UserProfile from "@/domain/entities/user/UserProfile";
import sanitizeHTML from "@/lib/sanitizeHTML";
import {
    AgendaItemDateTime,
    AgendaItem as AgendaItemSDK,
} from "@cs_teamwaydev/mailvista-sdk_react";
import agendaItemAttendeeToDomainMapper from "./agendaItemAttendeeMapper";
import agendaItemLocationToDomainMapper from "./agendaItemLocationMapper";
import agendaItemOrganizerToDomainMapper from "./agendaItemOrganizerMapper";
import agendaOnlineMeetingToDomainMapper from "./agendaOnlineMeetingMapper";

const cleanBody = (body: string): string => {
    if (!body) return "";

    let cleanedBody = body;

    // Supprimer seulement les propriétés CSS de couleur dans les attributs style
    cleanedBody = cleanedBody.replace(
        /\s*style\s*=\s*["']([^"']*)["']/gi,
        (_, styleContent) => {
            // Supprimer les propriétés de couleur et white-space: nowrap du contenu style
            const cleanedStyle = styleContent
                .replace(/\s*color\s*:\s*[^;]+;?/gi, "")
                .replace(/\s*white-space\s*:\s*nowrap\s*;?/gi, "")
                .replace(/;\s*;/g, ";") // Nettoyer les points-virgules doubles
                .trim();

            // Si le style est vide après nettoyage, supprimer l'attribut style entier
            if (!cleanedStyle) {
                return "";
            }

            return ` style="${cleanedStyle}"`;
        },
    );

    return cleanedBody.trim();
};

// If an event is created by an outlook user, he is not considered as an attendee, unlike other organizers from other providers. So we add missing organizers to the list for consistency.
const addOrganizerToAttendees = (
    attendees: AgendaItemAttendee[],
    organizer?: AgendaItemOrganizer,
) => {
    const attendeesWithOrganizer = [...attendees];

    if (
        organizer &&
        !attendees.some(
            (attendee) =>
                attendee.emailAddress.address.toLowerCase() ===
                organizer?.emailAddress?.address?.toLowerCase(),
        )
    ) {
        attendeesWithOrganizer.unshift(
            new AgendaItemAttendee({
                emailAddress: new AgendaEmailAddress({
                    name: organizer.emailAddress.name,
                    address: organizer.emailAddress.address,
                }),
            }),
        );
    }

    return attendeesWithOrganizer;
};

// Converts an ISO 8601 date string to a Date object in UTC
const parseUTCDate = (dateTimeString: AgendaItemDateTime): Date => {
    if (dateTimeString.timeZone === "UTC") {
        if (
            dateTimeString.dateTime.endsWith("Z") || // The date is already in UTC
            dateTimeString.dateTime.endsWith("00:00:00.0000000") // The date is an allDay event and should not be converted to UTC
        ) {
            return new Date(dateTimeString.dateTime);
        }

        return new Date(`${dateTimeString.dateTime}Z`);
    }
    throw new Error(`timeZone ${dateTimeString.timeZone} is not supported`);
};

const agendaItemToDomainMapper = (agendaItem: AgendaItemSDK): AgendaItem =>
    new AgendaItem({
        id: agendaItem.id,
        subject: agendaItem.subject,
        start: parseUTCDate(agendaItem.start),
        end: parseUTCDate(agendaItem.end),
        body: agendaItem.body ? cleanBody(sanitizeHTML(agendaItem.body)) : "",
        bodyPreview: agendaItem.bodyPreview ?? "",
        location: agendaItem.location
            ? agendaItemLocationToDomainMapper(agendaItem.location)
            : undefined,
        attendees: addOrganizerToAttendees(
            agendaItem.attendees.map(agendaItemAttendeeToDomainMapper),
            agendaItem.organizer
                ? agendaItemOrganizerToDomainMapper(agendaItem.organizer)
                : undefined,
        ),
        organizer: agendaItem.organizer
            ? agendaItemOrganizerToDomainMapper(agendaItem.organizer)
            : undefined,
        categories: agendaItem.categories,
        onlineMeeting: agendaItem.onlineMeeting
            ? agendaOnlineMeetingToDomainMapper(agendaItem.onlineMeeting)
            : undefined,
        isAllDay: agendaItem.isAllDay,
        isCancelled: agendaItem.isCancelled,
        importance: agendaItem.importance as AgendaItemImportance,
        sensitivity: agendaItem.sensitivity as AgendaItemSensitivity,
        showAs: agendaItem.showAs as AgendaItemShowAs,
        type: agendaItem.type as AgendaItemType,
        webLink: agendaItem.webLink,
        createdDateTime: new Date(agendaItem.createdDateTime),
        lastModifiedDateTime: new Date(agendaItem.lastModifiedDateTime),
    });

export default agendaItemToDomainMapper;

export const createAgendaItemDataToAgendaItemMapper = (
    createAgendaItemData: CreateAgendaItemData,
    organizer?: UserProfile,
): AgendaItem =>
    new AgendaItem({
        id: "",
        subject: createAgendaItemData.subject,
        start: new Date(createAgendaItemData.start),
        end: new Date(createAgendaItemData.end),
        body: "",
        bodyPreview: createAgendaItemData.description,
        attendees: createAgendaItemData.attendees
            ? createAgendaItemData.attendees?.map(
                  (attendee) =>
                      new AgendaItemAttendee({
                          emailAddress: new AgendaEmailAddress({
                              name: "",
                              address: attendee,
                          }),
                      }),
              )
            : [],
        location: {
            displayName: createAgendaItemData.location ?? "",
            locationType: "",
            uniqueIdType: "",
        },
        isAllDay: createAgendaItemData.isAllDay ?? false,
        webLink: "",
        createdDateTime: new Date(),
        lastModifiedDateTime: new Date(),
        importance: "normal",
        sensitivity: "normal",
        organizer: organizer
            ? new AgendaItemOrganizer({
                  emailAddress: new AgendaEmailAddress({
                      name: organizer.profile?.displayName ?? "",
                      address: organizer.profile?.email ?? "",
                  }),
              })
            : undefined,
        onlineMeeting: undefined,
        categories: [],
        isCancelled: false,
        showAs: "busy",
        type: "singleInstance",
    });
